<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import Message from 'primevue/message';
import Textarea from 'primevue/textarea';
import { computed, useAttrs, type Ref } from 'vue';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpTextAreaProps = {
    label?: string;
    inputId?: string;
    inputClass?: string | object;
    error?: string | null;
};

const value: Ref<any> = defineModel();
const props = withDefaults(defineProps<KpTextAreaProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    error: '',
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
}>();

const cls = useAttrs().class || null;
const fieldAttrs = computed(() => {
    const returnObj = { ...useAttrs() };
    // Remove attributes for the inner field we do NOT want to pass along:
    delete returnObj.id;
    delete returnObj.class;
    delete returnObj.label;
    delete returnObj['input-id'];
    delete returnObj['input-class'];
    delete returnObj['input-type'];
    return returnObj;
});

function onChange(value: any) {
    emit('change', value);
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <Textarea
            :id="inputId"
            v-model="value"
            :class="inputClass"
            v-bind="fieldAttrs"
            @value-change="onChange"
        ></Textarea>
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
